<template>
  <div class="step">
    <div class="step-prompt">
      <div v-if="isPromptEmpty" class="step-prompt-placeholder">
        <template v-if="placeholder">{{ placeholder }}</template>
        <template v-else>
          Use <code>/</code> for commands and <code>@</code> to link objects and
          integrations.
        </template>
      </div>
      <Editor
        v-if="workspaceId"
        ref="editor"
        :notes="promptDoc"
        :workspace-id="workspaceId"
        :integration-organization-ids="integrationOrganizationIds"
        :org-id="orgId"
        rect-container-selector=".step-prompt"
        :editable="true"
        @change="onPromptChange"
      />
    </div>
    <IntegrationPillsBar
      class="step-integration-pills"
      :workspace-id="workspaceId"
      :organization-ids="integrationOrganizationIds"
      @object-click="onObjectPillClick"
      @integration-click="onIntegrationPillClick"
    />
  </div>
</template>

<script lang="ts">
import type { Node as ProseMirrorNode } from "prosemirror-model";
import {
  computed,
  defineAsyncComponent,
  defineComponent,
  onMounted,
  provide,
  ref,
  shallowRef,
  toRefs,
  watch,
  type PropType,
} from "vue";

import type {
  AiPrompt,
  AiStep,
  Document,
  OrgId,
  StepId,
} from "@ctrl/controller-rpc";

import IntegrationPillsBar, {
  integrationConnectionId,
  type IntegrationPill,
} from "@/components/ai/IntegrationPillsBar.vue";
import { resolvePromptContext } from "@/components/editor/context";
import {
  documentToProseMirrorDoc,
  proseMirrorDocToDocument,
} from "@/components/editor/document-to-prosemirror";
import type { EditorInsertHandle } from "@/components/editor/Editor.vue";
import { isDefaultEmptyDoc } from "@/components/editor/utils";
import * as inject from "@/components/inject";
import templates, { instantiate } from "@/components/templates";
import { useDocumentMerge } from "@/composables/useDocumentMerge";
import { bindAsync, releasableRef, useReleaser } from "@/ref";

import { isPromptDocumentEmpty } from "./stepPrompt";

const Editor = defineAsyncComponent(
  () => import("@/components/editor/Editor.vue")
);

export default defineComponent({
  name: "Step",
  components: { Editor, IntegrationPillsBar },
  props: {
    stepId: { type: String, required: true },
    organizationId: { type: String, required: true },
    workspaceId: { type: String, required: true },
    autoSave: { type: Boolean, default: true },
    /** Overrides the default "Use / for commands…" placeholder when set. */
    placeholder: { type: String, default: undefined },
    /**
     * Organizations whose integrations the step's editor and pills bar may
     * use. `undefined` keeps the editor's workspace-scoped default.
     */
    integrationOrganizationIds: {
      type: Array as PropType<OrgId[] | undefined>,
      default: undefined,
    },
    /**
     * The organization the editor's `[[` / `@` reference searches offer
     * (see the `Editor` prop of the same name): an `OrgId`, `null` for all
     * organizations, or `undefined` to derive it from the workspace.
     */
    orgId: {
      type: String as PropType<OrgId | null | undefined>,
      required: false,
      default: undefined,
    },
  },
  emits: {
    dirty: () => true,
  },
  async setup(props, { emit }) {
    const { stepId: stepIdRef, organizationId: organizationIdRef } =
      toRefs(props);
    const stepId = computed(() => stepIdRef.value satisfies StepId);
    const organizationId = computed(
      () => organizationIdRef.value satisfies OrgId
    );
    const controller = inject.controller();
    const logger = inject.logger("Step");
    const baseLogger = logger.base();
    const integrations = inject.integrations();
    const resolveConnectionOrgId = (connectionId: string) =>
      integrations.organizationIdForConnection(connectionId);

    const releaser = useReleaser();

    // Pill clicks append into the step's prompt editor.
    const editor = ref<EditorInsertHandle | null>(null);
    const onObjectPillClick = () => {
      editor.value?.insertTextAtEnd?.("[[");
    };
    const onIntegrationPillClick = (pill: IntegrationPill) => {
      editor.value?.insertIntegrationAtEnd?.({
        integrationKey: pill.integration.integrationKey,
        connectionId: integrationConnectionId(pill.integration),
        name: pill.name,
      });
    };

    provide(
      "parent",
      computed(() =>
        stepId.value ? { kind: "object" as const, id: stepId.value } : null
      )
    );

    const stepData = shallowRef<AiStep | null | undefined>(undefined);

    // Undefined until the step loads: useDocumentMerge adopts the first real
    // document instead of merging it against an empty placeholder.
    const remotePrompt = computed<Document | undefined>(
      () => stepData.value?.action.prompt.document
    );

    const latestPmDoc = shallowRef<ProseMirrorNode | null>(null);

    async function pushPromptToController(doc: Document) {
      if (!organizationId.value) return;
      // When no editor produced a ProseMirror doc this session, derive one
      // from the document being saved: persisting an empty context instead
      // would destroy whatever context the step prompt had stored.
      const pmDoc =
        latestPmDoc.value ?? documentToProseMirrorDoc(doc, baseLogger);
      const prompt: AiPrompt = {
        context: await resolvePromptContext(
          controller,
          pmDoc,
          ["integrations", "mentions", "references"],
          baseLogger,
          resolveConnectionOrgId
        ),
        document: doc,
      };
      // Awaited (through the push hook below) so useDocumentMerge's mutex
      // covers the whole save RPC (see `editor-document-merging.md`,
      // "Merging / push race condition").
      await controller.ai.step.update(organizationId.value, stepId.value, {
        prompt,
      });
    }

    // Two modes, mirroring the autosave/commit split in GraphNotes:
    //
    // AUTOSAVE: useDocumentMerge with debounced push + 3-way merge.
    // DRAFT:    plain local ref, no merge, no push until explicit save().
    //
    // Autosave setup + lifecycle hooks must be registered before await.
    const mergeResult = props.autoSave
      ? useDocumentMerge(
          remotePrompt,
          controller,
          baseLogger,
          (doc) => pushPromptToController(doc),
          { label: `Step/prompt@${stepId.value}` }
        )
      : null;

    if (mergeResult) {
      const pendingChangeFlushes = inject.pendingChangeFlushes();
      onMounted(() => pendingChangeFlushes.add(mergeResult.flush));
      releaser.defer(() => {
        pendingChangeFlushes.delete(mergeResult.flush);
        mergeResult.flush();
        mergeResult.release();
      });
    }

    const stepRef = await releaser.add(
      bindAsync(stepId, async (id) => {
        if (!id || !organizationId.value)
          return releasableRef<AiStep | null>(null);
        return controller.ai.step.get(organizationId.value, id);
      })
    );

    stepData.value = stepRef.value;
    releaser.defer(
      watch(stepRef, (v) => {
        stepData.value = v;
      })
    );

    if (mergeResult) {
      const { localDocument: promptDoc, handleLocalChange } = mergeResult;

      const onPromptChange = (
        proseMirrorDoc: ProseMirrorNode,
        shouldFlush?: boolean
      ) => {
        latestPmDoc.value = proseMirrorDoc;
        handleLocalChange(
          proseMirrorDocToDocument(proseMirrorDoc),
          shouldFlush
        );
      };

      const isPromptEmpty = computed(() => {
        if (latestPmDoc.value !== null)
          return isDefaultEmptyDoc(latestPmDoc.value);
        return isPromptDocumentEmpty(promptDoc.value);
      });

      return {
        promptDoc,
        onPromptChange,
        isPromptEmpty,
        editor,
        onObjectPillClick,
        onIntegrationPillClick,
      };
    } else {
      const promptDoc = ref<Document>(remotePrompt.value ?? { blocks: [] });

      const onPromptChange = (proseMirrorDoc: ProseMirrorNode) => {
        latestPmDoc.value = proseMirrorDoc;
        promptDoc.value = proseMirrorDocToDocument(proseMirrorDoc);
        emit("dirty");
      };

      const save = () => {
        // The backend rejects empty prompts. If the user cleared the editor,
        // re-seed it with an empty paragraph so the save succeeds.
        const doc: Document =
          promptDoc.value.blocks.length === 0
            ? { blocks: instantiate(templates.empty) }
            : promptDoc.value;
        promptDoc.value = doc;
        void pushPromptToController(doc);
      };

      const isPromptEmpty = computed(() => {
        if (latestPmDoc.value !== null)
          return isDefaultEmptyDoc(latestPmDoc.value);
        return isPromptDocumentEmpty(promptDoc.value);
      });

      return {
        promptDoc,
        onPromptChange,
        isPromptEmpty,
        save,
        editor,
        onObjectPillClick,
        onIntegrationPillClick,
      };
    }
  },
});
</script>

<style scoped lang="scss">
.step {
  width: 100%;
  display: flex;
  flex-direction: column;

  // Tuck the bar under the prompt box (which is positioned, so it paints on
  // top).
  .step-integration-pills {
    margin-block-start: -10px;
    margin-inline: 0;
  }

  .step-prompt {
    position: relative;
    width: 100%;
    min-height: 100px;
    // Clip the editor's built-in after-content click target to this prompt box.
    overflow: hidden;
    border-radius: $border-radius-m;
    @include themed() {
      background-color: t($grey-05);
    }

    .step-prompt-placeholder {
      position: absolute;
      top: 0;
      inset-inline-start: 0;
      inset-inline-end: 0;
      padding: 8px 10px;
      pointer-events: none;
      @extend %font-body-regular;
      @include themed() {
        color: t($grey-40);
      }

      code {
        @extend %inline-code-chip;
      }
    }
  }
}
</style>
